"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useEffectEvent, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useAnalytics, getEvent } from "@/lib/analytics";
import type { AnnouncementData, AddonAccentColors } from "../model/types";
import { parseArcadeUrl } from "../lib/arcade-utils";
import { ArcadeEmbed } from "./arcade-embed";
interface TutorialModalProps {
  readonly announcement: AnnouncementData;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly featureKey?: string;
  readonly variant?: "default" | "preview";
  /** Addon accent colors — when provided, the modal gradient uses addon branding */
  readonly addonAccent?: AddonAccentColors;
}
export function TutorialModal(t0) {
  const $ = _c(53);
  const {
    announcement,
    isOpen,
    onClose,
    featureKey,
    variant: t1,
    addonAccent
  } = t0;
  t1 === undefined ? "default" : t1;
  const [currentStep, setCurrentStep] = useState(0);
  const modalRef = useRef(null);
  const {
    track
  } = useAnalytics();
  const totalSteps = announcement.steps.length;
  const step = announcement.steps[currentStep];
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) {
      setCurrentStep(0);
    }
  }
  let t2;
  if ($[0] !== announcement.steps.length || $[1] !== announcement.title || $[2] !== featureKey || $[3] !== track) {
    t2 = () => {
      track(getEvent("feature-announcement", "announcement_viewed"), {
        featureKey,
        announcementTitle: announcement.title,
        stepCount: announcement.steps.length
      });
    };
    $[0] = announcement.steps.length;
    $[1] = announcement.title;
    $[2] = featureKey;
    $[3] = track;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const trackViewed = useEffectEvent(t2);
  let t3;
  if ($[5] !== isOpen || $[6] !== trackViewed) {
    t3 = () => {
      if (isOpen) {
        trackViewed();
      }
    };
    $[5] = isOpen;
    $[6] = trackViewed;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== isOpen) {
    t4 = [isOpen];
    $[8] = isOpen;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[10] !== isOpen) {
    t5 = () => {
      if (!isOpen) {
        return;
      }
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    };
    t6 = [isOpen];
    $[10] = isOpen;
    $[11] = t5;
    $[12] = t6;
  } else {
    t5 = $[11];
    t6 = $[12];
  }
  useEffect(t5, t6);
  let t7;
  if ($[13] !== currentStep || $[14] !== featureKey || $[15] !== onClose || $[16] !== totalSteps || $[17] !== track) {
    t7 = () => {
      if (currentStep < totalSteps - 1) {
        const nextStep = currentStep + 1;
        setCurrentStep(nextStep);
        track(getEvent("feature-announcement", "step_viewed"), {
          featureKey,
          stepIndex: nextStep,
          totalSteps
        });
      } else {
        track(getEvent("feature-announcement", "announcement_completed"), {
          featureKey,
          stepsViewed: currentStep + 1
        });
        onClose();
      }
    };
    $[13] = currentStep;
    $[14] = featureKey;
    $[15] = onClose;
    $[16] = totalSteps;
    $[17] = track;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const goNext = t7;
  let t8;
  if ($[19] !== currentStep || $[20] !== featureKey || $[21] !== totalSteps || $[22] !== track) {
    t8 = () => {
      if (currentStep > 0) {
        setCurrentStep(_temp);
        track(getEvent("feature-announcement", "step_viewed"), {
          featureKey,
          stepIndex: currentStep - 1,
          totalSteps
        });
      }
    };
    $[19] = currentStep;
    $[20] = featureKey;
    $[21] = totalSteps;
    $[22] = track;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const goPrev = t8;
  let t9;
  if ($[24] !== currentStep || $[25] !== featureKey || $[26] !== onClose || $[27] !== track) {
    t9 = () => {
      track(getEvent("feature-announcement", "announcement_dismissed"), {
        featureKey,
        lastStepViewed: currentStep
      });
      onClose();
    };
    $[24] = currentStep;
    $[25] = featureKey;
    $[26] = onClose;
    $[27] = track;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const handleDismiss = t9;
  let t10;
  let t11;
  if ($[29] !== goNext || $[30] !== goPrev || $[31] !== handleDismiss || $[32] !== isOpen) {
    t10 = () => {
      if (!isOpen) {
        return;
      }
      const handleKey = function handleKey(e) {
        if (e.key === "Escape") {
          handleDismiss();
        } else {
          if (e.key === "ArrowRight") {
            goNext();
          } else {
            if (e.key === "ArrowLeft") {
              goPrev();
            } else {
              if (e.key === "Tab") {
                const modal = modalRef.current;
                if (!modal) {
                  return;
                }
                const focusable = modal.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])");
                if (focusable.length === 0) {
                  return;
                }
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                  e.preventDefault();
                  last.focus();
                } else {
                  if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                  }
                }
              }
            }
          }
        }
      };
      window.addEventListener("keydown", handleKey);
      return () => window.removeEventListener("keydown", handleKey);
    };
    t11 = [isOpen, handleDismiss, goNext, goPrev];
    $[29] = goNext;
    $[30] = goPrev;
    $[31] = handleDismiss;
    $[32] = isOpen;
    $[33] = t10;
    $[34] = t11;
  } else {
    t10 = $[33];
    t11 = $[34];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[35] !== isOpen) {
    t12 = () => {
      if (isOpen && modalRef.current) {
        modalRef.current.focus();
      }
    };
    t13 = [isOpen];
    $[35] = isOpen;
    $[36] = t12;
    $[37] = t13;
  } else {
    t12 = $[36];
    t13 = $[37];
  }
  useEffect(t12, t13);
  let t14;
  if ($[38] !== addonAccent || $[39] !== announcement.badgeText || $[40] !== announcement.steps || $[41] !== announcement.title || $[42] !== currentStep || $[43] !== featureKey || $[44] !== goNext || $[45] !== handleDismiss || $[46] !== isOpen || $[47] !== step || $[48] !== totalSteps || $[49] !== track) {
    t14 = isOpen && step && <motion.div className="fixed inset-0 z-2000 flex items-center justify-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }}><div className="absolute inset-0 bg-black/60 backdrop-blur-xs" onClick={handleDismiss} aria-hidden="true" /><motion.div ref={modalRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={announcement.title} className="relative z-10 mx-4 flex w-full min-w-[760px] max-w-[760px] max-h-[586px] flex-col overflow-hidden rounded-2xl shadow-2xl outline-hidden" style={{
        background: addonAccent ? `linear-gradient(90deg, ${addonAccent.from} 0%, ${addonAccent.to} 50%, ${addonAccent.from} 100%)` : "linear-gradient(90deg, #4f46e5 0%, #38bdf8 50%, #4f46e5 100%)"
      }} initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.25,
        ease: "easeOut"
      }}><span className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")"
        }} aria-hidden="true" /><button type="button" onClick={handleDismiss} aria-label="Close tutorial" className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-transparent text-white transition-colors hover:bg-white/15"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg></button>{announcement.badgeText && <div className="flex justify-center pt-7"><span className="rounded-full bg-white/85 px-4 py-1.5 text-[0.7rem] font-bold uppercase tracking-widest text-gray-900">{announcement.badgeText}</span></div>}<div className="relative min-h-0 flex-1 overflow-hidden px-8 pt-6"><div key={currentStep} className="animate-tutorial-step-in h-full">{(() => {
              const arcadeEmbed = step.mediaType === "video" && step.mediaUrl ? parseArcadeUrl(step.mediaUrl) : null;
              if (arcadeEmbed) {
                return <ArcadeEmbed arcadeUrl={arcadeEmbed.embedUrl} title={step.description} />;
              }
              if (step.mediaType === "video" && step.mediaUrl) {
                return <div className="relative overflow-hidden rounded-t-xl"><div className="flex h-48 items-center justify-center bg-gray-900/80"><button type="button" onClick={() => {
                      track(getEvent("feature-announcement", "video_played"), {
                        featureKey,
                        stepIndex: currentStep,
                        mediaType: step.mediaType
                      });
                      if (step.mediaUrl) {
                        window.open(step.mediaUrl, "_blank", "noopener,noreferrer");
                      }
                    }} className="flex items-center justify-center"><div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-lg"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="ml-0.5"><polygon points="5,3 19,12 5,21" fill="currentColor" className="text-gray-900" /></svg></div></button></div></div>;
              }
              if (step.mediaType === "image" && step.mediaUrl) {
                return <div className="relative max-h-[360px] overflow-hidden rounded-t-xl"><img src={step.mediaUrl} alt={`Step ${currentStep + 1}`} className="h-full w-full object-contain object-center" /></div>;
              }
              return <div className="flex h-48 items-center justify-center rounded-t-xl bg-white/10"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="text-white/40" aria-hidden="true"><path d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z" fill="currentColor" /></svg></div>;
            })()}</div></div><div className="w-full shrink-0"><div className="bg-white px-6 pt-5"><p key={`desc-${currentStep}`} className="animate-tutorial-step-in text-left text-[0.9rem] leading-relaxed text-gray-600">{step.description}</p></div><nav className="flex items-center justify-between rounded-b-2xl bg-white px-6 pb-5 pt-4"><button type="button" onClick={handleDismiss} className="text-[0.8rem] font-medium text-gray-400 transition-colors hover:text-gray-600">Skip</button><div className="flex items-center gap-1.5" role="tablist" aria-label="Tutorial steps">{announcement.steps.map((_, i) => <button key={i} type="button" role="tab" aria-selected={i === currentStep} aria-label={`Step ${i + 1}`} onClick={() => {
                setCurrentStep(i);
                track(getEvent("feature-announcement", "step_viewed"), {
                  featureKey,
                  stepIndex: i,
                  totalSteps
                });
              }} className={["h-2 w-2 rounded-full transition-all duration-200", i === currentStep ? "scale-125 bg-[#4f46e5]" : "bg-gray-300 hover:bg-gray-400"].join(" ")} />)}</div><button type="button" onClick={goNext} className="rounded-lg bg-[#4f46e5] px-5 py-2 text-[0.8rem] font-semibold text-white shadow-md shadow-indigo-500/40 transition-colors hover:bg-[#4338ca]">{currentStep === totalSteps - 1 ? "Done" : "Next"}</button></nav></div></motion.div></motion.div>;
    $[38] = addonAccent;
    $[39] = announcement.badgeText;
    $[40] = announcement.steps;
    $[41] = announcement.title;
    $[42] = currentStep;
    $[43] = featureKey;
    $[44] = goNext;
    $[45] = handleDismiss;
    $[46] = isOpen;
    $[47] = step;
    $[48] = totalSteps;
    $[49] = track;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  let t15;
  if ($[51] !== t14) {
    t15 = <AnimatePresence>{t14}</AnimatePresence>;
    $[51] = t14;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  return t15;
}
function _temp(s) {
  return s - 1;
}